import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Target, Navigation as NavigationIcon, Palette, ShieldCheck } from "lucide-react";
export const BouncyCardsFeatures = () => {
  const features = [{
    icon: MessageSquare,
    title: "Voice & Tone",
    description: "Your signature language; captured, refined, and applied automatically.",
    gradient: "from-purple-500/20 to-pink-500/20",
    iconBg: "bg-gradient-to-br from-purple-500 to-pink-500"
  }, {
    icon: Target,
    title: "Audience",
    description: "Knows who you're speaking to and what resonates most.",
    gradient: "from-green-500/20 to-emerald-500/20",
    iconBg: "bg-gradient-to-br from-green-500 to-emerald-500"
  }, {
    icon: NavigationIcon,
    title: "Messaging",
    description: "Keeps every campaign true to your brand promise.",
    gradient: "from-blue-500/20 to-cyan-500/20",
    iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500"
  }, {
    icon: Palette,
    title: "Visual Identity",
    description: "Protects your look; colours, fonts, and creative mood.",
    gradient: "from-orange-500/20 to-red-500/20",
    iconBg: "bg-gradient-to-br from-orange-500 to-red-500"
  }, {
    icon: ShieldCheck,
    title: "Brand Guard",
    description: "Locks compliance and brand rules into every output.",
    gradient: "from-indigo-500/20 to-violet-500/20",
    iconBg: "bg-gradient-to-br from-indigo-500 to-violet-500"
  }];
  return <section className="mx-auto max-w-7xl py-0">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} className="mb-12 flex flex-col items-center gap-4">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-foreground mb-4">Creating on-brand marketing content</h2>
        <p className="text-xl text-muted-foreground max-w-3xl text-center">Knowing your brand is just the first step of your gimmefy journey. Our AI puts together information gathered about you, along with your prompts and briefs to make every output sound, look, and feel exactly like you.</p>
      </motion.div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
        {features.map((feature, index) => <BounceCard key={index} className="col-span-1" feature={feature} />)}
      </div>
      
      {/* Result Callout */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: 0.3
    }} className="text-center mt-8">
        <div className="inline-block p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20">
          <p className="text-base md:text-lg font-semibold">
            <span className="text-foreground">Result: </span>
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Every post, ad, and campaign stays on-brand — by default, not by accident.
            </span>
          </p>
        </div>
      </motion.div>
    </section>;
};
interface BounceCardProps {
  className: string;
  feature: {
    icon: React.ElementType;
    title: string;
    description: string;
    gradient: string;
    iconBg: string;
  };
}
const BounceCard = ({
  className,
  feature
}: BounceCardProps) => {
  const Icon = feature.icon;
  return <motion.div whileHover={{
    scale: 0.95,
    rotate: "-1deg"
  }} className={`group relative min-h-[280px] cursor-pointer overflow-hidden rounded-2xl bg-gradient-to-br ${feature.gradient.replace('/20', '')} p-6 border border-white/20 ${className}`}>
      <CardTitle>{feature.title}</CardTitle>
      <div className="absolute bottom-0 left-3 right-3 top-14 translate-y-8 rounded-t-2xl bg-white p-4 transition-transform duration-[250ms] group-hover:translate-y-4 group-hover:rotate-[2deg] flex flex-col items-center justify-center gap-3 shadow-lg">
        <div className={`w-14 h-14 rounded-xl ${feature.iconBg} flex items-center justify-center shadow-lg`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        <p className="text-center font-medium text-foreground text-xs md:text-sm leading-relaxed">
          {feature.description}
        </p>
      </div>
    </motion.div>;
};
const CardTitle = ({
  children
}: {
  children: React.ReactNode;
}) => {
  return <h3 className="mx-auto text-center text-base md:text-lg lg:text-xl font-bold text-white mb-2">{children}</h3>;
};